
import { useState, useEffect } from 'react';
import { Phone, X, PhoneCall, Mic, MicOff, Signal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TwilioDialer } from '@/components/dialer/TwilioDialer';
import { useTwilioContext } from '@/context/TwilioContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation } from 'react-router-dom';

export const PhoneDialerButton = () => {
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  const { callInProgress, callStatus, formattedDuration, micPermission, requestPermissions } = useTwilioContext();
  
  // Close dialer when pressing escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDialerOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);
  
  // Get status display text and colors
  const getStatusDisplay = () => {
    if (!callInProgress) {
      return null;
    }
    
    switch (callStatus) {
      case 'connecting':
        return { text: 'Connecting...', color: 'bg-yellow-500', animate: true };
      case 'ringing':
        return { text: 'Ringing...', color: 'bg-blue-500', animate: true };
      case 'open':
        return { text: formattedDuration || '00:00', color: 'bg-green-500', animate: false };
      default:
        return { text: 'In Call', color: 'bg-green-500', animate: false };
    }
  };
  
  // Render microphone permission icon based on permission status
  const renderMicrophoneIndicator = () => {
    if (micPermission === 'denied') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="ml-2 bg-red-50 text-red-700 border-red-200 flex items-center">
                <MicOff size={14} className="mr-1" />
                <span className="hidden sm:inline">Mic access denied</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Microphone access is required for calls. Check your browser permissions.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  };
  
  const handleDialerOpen = () => {
    // If microphone permission is denied, try to request it again
    if (micPermission === 'denied' && !isDialerOpen) {
      requestPermissions();
    }
    setIsDialerOpen(!isDialerOpen);
  };

  const statusDisplay = getStatusDisplay();

  const shouldShow = callInProgress || location.pathname.startsWith('/app');

  // Show if there's an active call OR if we're on an app route
  if (!shouldShow) {
    return null;
  }

  // For mobile, use Sheet instead of Popover
  if (isMobile) {
    return (
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn(
            "h-8 w-8 rounded-full relative",
            callInProgress && "bg-green-100 dark:bg-green-900/20"
          )}
          onClick={handleDialerOpen}
          aria-label="Phone Dialer"
        >
          {callInProgress ? (
            <PhoneCall size={16} className="text-green-500" />
          ) : (
            <Phone size={16} />
          )}
          {callInProgress && (
            <span className={cn(
              "absolute top-0 right-0 h-2 w-2 rounded-full",
              statusDisplay?.color || "bg-green-500",
              statusDisplay?.animate && "animate-pulse"
            )} />
          )}
          {micPermission === 'denied' && !callInProgress && (
            <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" />
          )}
        </Button>
        
        {/* Mobile status badge - show when call is active */}
        {callInProgress && statusDisplay && (
          <Badge 
            variant="outline" 
            className={cn(
              "ml-2 border flex items-center text-white font-medium text-xs",
              statusDisplay.color === 'bg-green-500' && "bg-green-500 border-green-600",
              statusDisplay.color === 'bg-blue-500' && "bg-blue-500 border-blue-600", 
              statusDisplay.color === 'bg-yellow-500' && "bg-yellow-500 border-yellow-600"
            )}
          >
            <Signal className="h-3 w-3 mr-1" />
            <span className="font-mono">{statusDisplay.text}</span>
            {statusDisplay.animate && (
              <span className="h-1.5 w-1.5 bg-white rounded-full animate-pulse ml-1" />
            )}
          </Badge>
        )}
        
        {renderMicrophoneIndicator()}
        
        <Sheet open={isDialerOpen} onOpenChange={setIsDialerOpen}>
          <SheetContent className="p-0 w-full sm:max-w-sm" side="right">
            <TwilioDialer onClose={() => setIsDialerOpen(false)} fullscreen={true} />
          </SheetContent>
        </Sheet>
      </div>
    );
  }
  
  // For desktop, use Popover
  return (
    <div className="flex items-center">
      <Popover open={isDialerOpen} onOpenChange={setIsDialerOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={cn(
              "h-8 w-8 rounded-full relative",
              callInProgress && "bg-green-100 dark:bg-green-900/20"
            )}
            aria-label="Phone Dialer"
          >
            {callInProgress ? (
              <PhoneCall size={16} className="text-green-500" />
            ) : (
              <Phone size={16} />
            )}
            {callInProgress && (
              <span className={cn(
                "absolute top-0 right-0 h-2 w-2 rounded-full",
                statusDisplay?.color || "bg-green-500",
                statusDisplay?.animate && "animate-pulse"
              )} />
            )}
            {micPermission === 'denied' && !callInProgress && (
              <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 border-none shadow-xl" align="end" side="bottom" sideOffset={5} 
          style={{ width: '320px' }}>
          <TwilioDialer onClose={() => setIsDialerOpen(false)} />
        </PopoverContent>
      </Popover>
      
      {/* Desktop status badge - show when call is active */}
      {callInProgress && statusDisplay && (
        <Badge 
          variant="outline" 
          className={cn(
            "ml-2 border flex items-center text-white font-medium",
            statusDisplay.color === 'bg-green-500' && "bg-green-500 border-green-600",
            statusDisplay.color === 'bg-blue-500' && "bg-blue-500 border-blue-600", 
            statusDisplay.color === 'bg-yellow-500' && "bg-yellow-500 border-yellow-600"
          )}
        >
          <Signal className="h-3 w-3 mr-1" />
          <span className="text-sm font-mono">{statusDisplay.text}</span>
          {statusDisplay.animate && (
            <span className="h-1.5 w-1.5 bg-white rounded-full animate-pulse ml-1" />
          )}
        </Badge>
      )}
      
      {renderMicrophoneIndicator()}
    </div>
  );
};
